import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";

import Home from "@/pages/Home";
import Article from "@/pages/Article";
import Category from "@/pages/Category";
import Categories from "@/pages/Categories";
import Latest from "@/pages/Latest";
import Search from "@/pages/Search";
import Digest from "@/pages/Digest";
import Author from "@/pages/Author";
import Institutions from "@/pages/Institutions";
import Institution from "@/pages/Institution";
import { About, EditorialPolicy, CorrectionPolicy } from "@/pages/Policy";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import Sources from "@/pages/admin/Sources";
import Feed from "@/pages/admin/Feed";
import Drafts from "@/pages/admin/Drafts";
import DraftEditor from "@/pages/admin/DraftEditor";
import AdminArticles from "@/pages/admin/Articles";
import AdminCache from "@/pages/admin/Cache";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--io-muted)] bg-[var(--io-bg)]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" state={{ from: loc }} replace />;
  return (
    <>
      <Seo title="Admin" path={loc.pathname} noIndex />
      {children}
    </>
  );
};

const Pub = ({ children }) => <Layout>{children}</Layout>;

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <Toaster position="top-right" theme="dark" richColors />
              <Routes>
                {/* Public */}
                <Route path="/" element={<Pub><Home /></Pub>} />
                <Route path="/latest" element={<Pub><Latest /></Pub>} />
                <Route path="/categories" element={<Pub><Categories /></Pub>} />
                <Route path="/article/:slug" element={<Pub><Article /></Pub>} />
                <Route path="/category/:slug" element={<Pub><Category /></Pub>} />
                <Route path="/institutions" element={<Pub><Institutions /></Pub>} />
                <Route path="/institution/:slug" element={<Pub><Institution /></Pub>} />
                <Route path="/search" element={<Pub><Search /></Pub>} />
                <Route path="/digest" element={<Pub><Digest /></Pub>} />
                <Route path="/author/:slug" element={<Pub><Author /></Pub>} />
                <Route path="/about" element={<Pub><About /></Pub>} />
                <Route path="/editorial-policy" element={<Pub><EditorialPolicy /></Pub>} />
                <Route path="/correction-policy" element={<Pub><CorrectionPolicy /></Pub>} />
                <Route path="/contact" element={<Pub><Contact /></Pub>} />

                {/* Admin */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
                <Route path="/admin/sources" element={<PrivateRoute><Sources /></PrivateRoute>} />
                <Route path="/admin/feed" element={<PrivateRoute><Feed /></PrivateRoute>} />
                <Route path="/admin/drafts" element={<PrivateRoute><Drafts /></PrivateRoute>} />
                <Route path="/admin/drafts/:id" element={<PrivateRoute><DraftEditor /></PrivateRoute>} />
                <Route path="/admin/articles" element={<PrivateRoute><AdminArticles /></PrivateRoute>} />
                <Route path="/admin/cache" element={<PrivateRoute><AdminCache /></PrivateRoute>} />

                <Route path="*" element={<Pub><NotFound /></Pub>} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
